import type { FC } from 'react'
import type { BookListItem } from '@/types'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { ArrowUpRight, Mail } from 'lucide-react'

import { useLocale, useT } from '@/i18n'
import { contact, getNav, socials } from '@/constants/germa'
import { Logo } from '@/components/landing/logo'
import { Container } from '@/components/generals/container'
import { InstallAppButton } from '@/components/generals/install-app-button'

const year = new Date().getFullYear()

export const Footer: FC = () => {
    const t = useT()
    const locale = useLocale()
    const quickLinks = getNav(t).filter((item) => item.href !== '/')
    const [featured, setFeatured] = useState<BookListItem[]>([])

    useEffect(() => {
        let active = true
        fetch(`/api/books?locale=${locale}`)
            .then((res) => res.json())
            .then((data) => {
                if (active && Array.isArray(data.books)) setFeatured(data.books.slice(0, 4))
            })
            .catch(() => {})
        return () => {
            active = false
        }
    }, [locale])

    return (
        <footer className='relative overflow-hidden bg-navy-deep text-parchment'>
            <div className='pointer-events-none absolute inset-0 bg-stars opacity-[0.05]' />
            <div className='pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent' />

            <Container size='wide' className='relative py-16 md:py-20'>
                <div className='grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.1fr]'>
                    <div className='flex flex-col gap-5'>
                        <Logo tone='parchment' />
                        <p className='max-w-xs text-sm leading-relaxed text-parchment/70'>
                            {t.footer.tagline}
                        </p>
                        <div className='flex items-center gap-3 pt-1'>
                            {socials.map((social) => (
                                <a
                                    key={social.label}
                                    href={social.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    aria-label={social.label}
                                    className='grid size-10 place-items-center rounded-full border border-parchment/20 text-parchment/75 transition-colors duration-300 hover:border-gold hover:text-gold-soft'
                                >
                                    <social.icon className='size-[1.15rem]' />
                                </a>
                            ))}
                        </div>
                        <InstallAppButton />
                    </div>

                    <div className='flex flex-col gap-4'>
                        <h3 className='text-xs font-semibold uppercase tracking-[0.24em] text-gold-soft'>
                            {t.footer.links}
                        </h3>
                        <ul className='flex flex-col gap-3'>
                            {quickLinks.map((item) => (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        className='group inline-flex items-center gap-1.5 text-sm text-parchment/75 transition-colors hover:text-parchment'
                                    >
                                        {item.label}
                                        <ArrowUpRight className='size-3.5 -translate-x-1 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100 rtl:-scale-x-100' />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='flex flex-col gap-4'>
                        <h3 className='text-xs font-semibold uppercase tracking-[0.24em] text-gold-soft'>
                            {t.footer.books}
                        </h3>
                        <ul className='flex flex-col gap-3'>
                            {featured.map((book) => (
                                <li key={book.slug}>
                                    <Link
                                        href={`/libra/${book.slug}`}
                                        data-mdlfy-cms={`books:${book.id}:${book.titleField ?? 'title'}`}
                                        className='text-sm text-parchment/75 transition-colors hover:text-parchment'
                                    >
                                        {book.title}
                                    </Link>
                                </li>
                            ))}
                            <li>
                                <Link
                                    href='/libra'
                                    className='text-sm font-medium text-gold-soft transition-colors hover:text-parchment'
                                >
                                    {t.footer.allBooks}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div className='flex flex-col gap-4'>
                        <h3 className='text-xs font-semibold uppercase tracking-[0.24em] text-gold-soft'>
                            {t.footer.contact}
                        </h3>
                        <p className='text-sm leading-relaxed text-parchment/70'>{t.footer.contactNote}</p>
                        <a
                            href={`mailto:${contact.email}`}
                            className='group inline-flex items-center gap-2 text-sm font-medium text-parchment transition-colors hover:text-gold-soft'
                        >
                            <Mail className='size-4 text-gold-soft' />
                            {contact.email}
                        </a>
                    </div>
                </div>

                <div className='mt-14 flex flex-col items-center justify-between gap-4 border-t border-parchment/10 pt-8 text-xs text-parchment/55 sm:flex-row'>
                    <p>© {year} Instituti Germa. {t.footer.rights}</p>
                </div>
            </Container>
        </footer>
    )
}
